import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Advisory",
  "Investment",
  "Execution",
  "Partnership",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    interest: "Advisory",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — we'll come back to you shortly.");
      setSent(true);
      setForm({
        name: "",
        email: "",
        company: "",
        interest: "Advisory",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        (Array.isArray(detail) && detail[0]?.msg) ||
        (typeof detail === "string" ? detail : null) ||
        "We couldn't send your message. Please try again.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B] mb-6">
          Contact
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter font-medium max-w-5xl">
          Tell us what you're trying to <em className="not-italic text-[#0F7A3E]">build</em>.
        </h1>
        <p className="mt-10 max-w-2xl text-lg text-[#575B57] leading-relaxed">
          The more specific, the better. A project, a thesis, a problem you
          can't get around — all useful. We reply to every serious message.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start border-t border-[#E4E5DF] pt-14">
          {/* FORM */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="md:col-span-7 space-y-10"
          >
            <Row>
              <Field label="Name *" htmlFor="name">
                <input
                  id="name"
                  value={form.name}
                  onChange={onChange("name")}
                  data-testid="contact-input-name"
                  required
                  className="input-saafi"
                  placeholder="Full name"
                />
              </Field>
              <Field label="Email *" htmlFor="email">
                <input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  data-testid="contact-input-email"
                  required
                  className="input-saafi"
                  placeholder="you@company.com"
                />
              </Field>
            </Row>

            <Row>
              <Field label="Company / Institution" htmlFor="company">
                <input
                  id="company"
                  value={form.company}
                  onChange={onChange("company")}
                  data-testid="contact-input-company"
                  className="input-saafi"
                  placeholder="Organisation"
                />
              </Field>
              <Field label="I'm interested in" htmlFor="interest">
                <select
                  id="interest"
                  value={form.interest}
                  onChange={onChange("interest")}
                  data-testid="contact-input-interest"
                  className="input-saafi appearance-none bg-transparent cursor-pointer"
                >
                  {INTERESTS.map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </Field>
            </Row>

            <Field label="Tell us what you're working on *" htmlFor="message">
              <textarea
                id="message"
                rows={6}
                value={form.message}
                onChange={onChange("message")}
                data-testid="contact-input-message"
                required
                className="input-saafi resize-none py-4"
                placeholder="A short brief — problem, context, timeline, what 'done' looks like."
              />
            </Field>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pt-6 border-t border-[#E4E5DF]">
              <p className="text-xs text-[#79836B] max-w-sm">
                By submitting, you consent to being contacted about your
                enquiry. We don't share your details.
              </p>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-4 bg-[#1A1C1A] text-[#FBFBF9] hover:bg-[#0F7A3E] px-7 py-5 text-sm transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? "Sending…" : sent ? "Sent — send another" : "Send message"}
                <ArrowUpRight
                  className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  strokeWidth={1.75}
                />
              </button>
            </div>

            {sent && (
              <p data-testid="contact-success-banner" className="text-sm text-[#0F7A3E]">
                Thank you. A member of our team will reply within two business days.
              </p>
            )}
          </form>

          {/* META */}
          <aside className="md:col-span-4 md:col-start-9 space-y-10">
            <MetaItem icon={Mail} label="Direct">
              <a href="mailto:sales@saafi.co.in" className="saafi-link">sales@saafi.co.in</a>
            </MetaItem>
            <MetaItem icon={Clock} label="Response time">
              Two business days for serious enquiries. <br />
              Same day for urgent transaction questions.
            </MetaItem>
          </aside>
        </div>
      </section>

      <style>{`
        .input-saafi {
          width: 100%;
          background-color: transparent;
          border: 0;
          border-bottom: 1px solid #E4E5DF;
          padding: 16px 0;
          font-size: 16px;
          color: #1A1C1A;
          outline: none;
          transition: border-color 200ms ease, color 200ms ease;
          border-radius: 0;
          font-family: inherit;
        }
        .input-saafi::placeholder { color: #9FA498; }
        .input-saafi:focus { border-bottom-color: #0F7A3E; }
      `}</style>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-8">{children}</div>;
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="text-[11px] uppercase tracking-[0.25em] text-[#79836B]">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function MetaItem({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Icon className="w-4 h-4 text-[#0F7A3E]" strokeWidth={1.5} />
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#79836B]">{label}</p>
      </div>
      <p className="text-[15px] leading-relaxed text-[#1A1C1A]">{children}</p>
    </div>
  );
}
